import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Edit as EditIcon,
  Map as MapIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { apiCall } from "../config/api";

const PinDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pin, setPin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const audioRef = useRef(null);

  useEffect(() => {
    fetchPinDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchPinDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiCall(`/api/pins/${id}`, { method: "GET" });
      if (response.success) {
        setPin(response.pin);
      } else {
        setError(response.error || "Pin bulunamadı");
      }
    } catch (err) {
      console.error("Error fetching pin:", err);
      setError("Pin detayları yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (audioPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setAudioPlaying(!audioPlaying);
  };

  const handleQuarantine = async () => {
    try {
      const response = await apiCall(`/api/pins/${id}/quarantine`, {
        method: "POST",
        body: JSON.stringify({ isQuarantined: !pin.isQuarantined }),
      });
      if (response.success) {
        setPin({ ...pin, isQuarantined: !pin.isQuarantined });
        alert(
          pin.isQuarantined
            ? "Pin karantinadan çıkarıldı"
            : "Pin karantinaya alındı"
        );
      }
    } catch (err) {
      alert("İşlem başarısız: " + err.message);
    }
  };

  const handlePromote = async () => {
    try {
      const response = await apiCall(`/api/pins/${id}/promote`, {
        method: "POST",
        body: JSON.stringify({ isPromoted: !pin.isPromoted }),
      });
      if (response.success) {
        setPin({ ...pin, isPromoted: !pin.isPromoted });
        alert(
          pin.isPromoted
            ? "Reklam durumu kaldırıldı"
            : "Pin reklam olarak işaretlendi"
        );
      }
    } catch (err) {
      alert("İşlem başarısız: " + err.message);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await apiCall(`/api/pins/${id}`, {
        method: "DELETE",
        body: JSON.stringify({ reason: deleteReason }),
      });
      if (response.success) {
        alert("Pin silindi");
        navigate("/pin-management");
      }
    } catch (err) {
      alert("Silme başarısız: " + err.message);
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !pin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || "Pin bulunamadı"}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/pin-management")}
          sx={{ mt: 2 }}
        >
          Geri Dön
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/pin-management")}
        >
          Geri
        </Button>
        <Typography variant="h4">
          {pin.title || pin.name || "Pin Detayı"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/pin-management?edit=${id}`)}
          >
            Düzenle
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Sil
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Stack spacing={3}>
          {/* Image */}
          {pin.imageUrl && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Pin Resmi
              </Typography>
              <Box
                component="img"
                src={pin.imageUrl}
                alt={pin.title || "Pin image"}
                sx={{
                  width: "100%",
                  maxWidth: 600,
                  height: "auto",
                  borderRadius: 2,
                  boxShadow: 3,
                }}
              />
            </Box>
          )}

          {/* Audio Player */}
          {pin.audioUrl && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Ses Kaydı
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <IconButton
                  onClick={handlePlayPause}
                  sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    "&:hover": { bgcolor: "primary.dark" },
                  }}
                >
                  {audioPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </IconButton>
                <audio
                  ref={audioRef}
                  src={pin.audioUrl}
                  onEnded={() => setAudioPlaying(false)}
                  onPlay={() => setAudioPlaying(true)}
                  onPause={() => setAudioPlaying(false)}
                  controls
                  style={{ flex: 1 }}
                />
              </Box>
            </Box>
          )}

          <Divider />

          {/* Basic Info */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Temel Bilgiler
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PersonIcon color="action" />
                <Typography>
                  <strong>Oluşturan:</strong>{" "}
                  {pin.pinOwnerName || pin.senderName || "Bilinmiyor"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CategoryIcon color="action" />
                <Typography>
                  <strong>Kategori:</strong> {pin.category || "Standard"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AccessTimeIcon color="action" />
                <Typography>
                  <strong>Oluşturulma:</strong>{" "}
                  {pin.createdAt
                    ? new Date(pin.createdAt).toLocaleString("tr-TR")
                    : "N/A"}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <MapIcon color="action" />
                <Typography>
                  <strong>Konum:</strong>{" "}
                  {pin.address || `${pin.latitude}, ${pin.longitude}`}
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Description */}
          {pin.description && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Açıklama
              </Typography>
              <Typography variant="body1">{pin.description}</Typography>
            </Box>
          )}

          {pin.textContent && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Metin İçerik
              </Typography>
              <Typography variant="body1">{pin.textContent}</Typography>
            </Box>
          )}

          <Divider />

          {/* Status Chips */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Durum
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                label={pin.status === "active" ? "Aktif" : "Pasif"}
                color={pin.status === "active" ? "success" : "default"}
              />
              <Chip
                label={pin.pinType === "audio" ? "Sesli" : "Metin"}
                color="primary"
                variant="outlined"
              />
              <Chip
                label={`Tier: ${pin.pinTier || "Free"}`}
                variant="outlined"
              />
              {pin.isPromoted && <Chip label="Reklam" color="warning" />}
              {pin.isQuarantined && <Chip label="Karantinada" color="error" />}
              {pin.isProtected && <Chip label="Korumalı" color="info" />}
            </Stack>
          </Box>

          <Divider />

          {/* Admin Actions */}
          <Box>
            <Typography variant="h6" gutterBottom>
              Yönetim İşlemleri
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Button
                variant="contained"
                color={pin.isQuarantined ? "success" : "error"}
                startIcon={
                  pin.isQuarantined ? <CheckCircleIcon /> : <BlockIcon />
                }
                onClick={handleQuarantine}
              >
                {pin.isQuarantined ? "Karantinadan Çıkar" : "Karantinaya Al"}
              </Button>
              <Button
                variant="contained"
                color={pin.isPromoted ? "inherit" : "warning"}
                onClick={handlePromote}
              >
                {pin.isPromoted ? "Reklam Kaldır" : "Reklam Yap"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<MapIcon />}
                onClick={() =>
                  window.open(
                    `https://www.google.com/maps?q=${pin.latitude},${pin.longitude}`,
                    "_blank"
                  )
                }
              >
                Haritada Aç
              </Button>
            </Stack>
          </Box>

          {/* Stats */}
          {pin.stats && (
            <Box>
              <Typography variant="h6" gutterBottom>
                İstatistikler
              </Typography>
              <Stack direction="row" spacing={3}>
                <Typography>
                  <strong>Görüntülenme:</strong> {pin.stats.totalViews || 0}
                </Typography>
                <Typography>
                  <strong>Çalma:</strong> {pin.stats.plays || 0}
                </Typography>
                <Typography>
                  <strong>Beğeni:</strong> {pin.likeIds?.length || 0}
                </Typography>
                <Typography>
                  <strong>Yorum:</strong> {pin.commentCount || 0}
                </Typography>
              </Stack>
            </Box>
          )}
        </Stack>
      </Paper>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Pin'i Sil</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Bu pin'i silmek istediğinizden emin misiniz?
          </Typography>
          <TextField
            fullWidth
            label="Silme Sebebi (Opsiyonel)"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            multiline
            rows={3}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>İptal</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PinDetailPage;
